import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  FileText,
  Clock,
  ArrowRight,
  Brain,
  Users,
  Calendar,
  Shield,
  Target,
  AlertCircle,
  Search,
  CheckCircle,
  Eye,
  Layers,
  MessageSquare,
  Zap,
  Award,
  ClipboardCheck,
  Download,
  Star,
  Train,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { PageLayout } from '../components/Layout';
import { SEO } from '../components/SEO';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45 },
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const categories = [
  'All',
  'Official Standard',
  'Guide',
  'Rules & Regulations',
  'Career Advice',
  'Inclusion',
  'Research',
];

const articles = [
  {
    slug: 'ris-3751-tom-explained',
    title: 'RIS-3751-TOM Explained: The Official Train Driver Assessment Standard',
    description:
      'A clear guide to the UK rail industry standard covering train driver psychometric assessment, selection criteria, attempt rules and assessment methods.',
    category: 'Official Standard',
    readTime: '15 min read',
    icon: Shield,
    featured: true,
    priority: 'Start here',
    tags: ['RIS-3751-TOM', 'RSSB', 'Assessment Standard'],
  },
  {
    slug: 'understanding-train-driver-psychometric-tests',
    title: 'Understanding Train Driver Psychometric Tests: The Complete Guide',
    description:
      'What the train driver psychometric process measures, why it exists, and how tests such as Group Bourdon, TEA-Occ, TRP, WAFV, ATAVT, 2HAND, SJE and MMI fit together.',
    category: 'Guide',
    readTime: '12 min read',
    icon: Brain,
    featured: true,
    priority: 'Most popular',
    tags: ['Psychometric Tests', 'Group Bourdon', 'MMI'],
  },
  {
    slug: 'train-driver-assessment-attempt-rules',
    title: 'Train Driver Assessment Attempt Rules: What Happens If You Fail?',
    description:
      'The current three-attempt structure, waiting periods, reassessment rules and what candidates should do after an unsuccessful attempt.',
    category: 'Rules & Regulations',
    readTime: '6 min read',
    icon: AlertCircle,
    featured: true,
    priority: 'Important',
    tags: ['Attempt Rules', 'Retakes', 'Reassessment'],
  },
  {
    slug: 'can-you-become-train-driver-at-18',
    title: 'Can You Become a Train Driver at 18? Age Requirements Explained',
    description:
      'What younger applicants need to understand about train driver recruitment, maturity, cognitive demands and future entry routes.',
    category: 'Career Advice',
    readTime: '8 min read',
    icon: Calendar,
    featured: false,
    priority: null,
    tags: ['Minimum Age', 'Career Entry', 'Future Drivers'],
  },
  {
    slug: 'neurodivergent-train-drivers-guide',
    title: 'Neurodivergent Train Drivers: Strengths, Challenges & Support',
    description:
      'A practical guide for candidates with ADHD, autism, dyslexia, dyspraxia or similar neurodivergent profiles, including disclosure and reasonable adjustments.',
    category: 'Inclusion',
    readTime: '10 min read',
    icon: Users,
    featured: false,
    priority: null,
    tags: ['Neurodiversity', 'Reasonable Adjustments', 'Support'],
  },
  {
    slug: 'mental-workload-train-drivers',
    title: 'Mental Workload in Train Driving: What Candidates Should Know',
    description:
      'Why train driving requires sustained attention, vigilance, memory, reasoning, perception, reaction time and disciplined communication.',
    category: 'Research',
    readTime: '7 min read',
    icon: Target,
    featured: false,
    priority: null,
    tags: ['Mental Workload', 'Cognitive Skills', 'Safety'],
  },
];

const skillAreas = [
  {
    title: 'Attention',
    text: 'Selective and divided attention, including switching between lineside information, in-cab information and operational tasks.',
    icon: Eye,
    tests: 'Group Bourdon, TEA-Occ',
  },
  {
    title: 'Vigilance',
    text: 'Maintaining readiness and responding correctly to infrequent stimuli over sustained periods.',
    icon: Target,
    tests: 'WAFV Vigilance',
  },
  {
    title: 'Memory & Reasoning',
    text: 'Learning rules, recalling instructions, applying procedures and solving operational problems.',
    icon: Brain,
    tests: 'TRP 1, TRP 2',
  },
  {
    title: 'Perception & Reaction',
    text: 'Processing visual information, anticipating hazards and responding quickly and accurately.',
    icon: Zap,
    tests: 'ATAVT, WAFV',
  },
  {
    title: 'Coordination',
    text: 'Controlled hand movements in response to complex stimuli and decisions.',
    icon: Layers,
    tests: '2HAND',
  },
  {
    title: 'Communication & Behaviour',
    text: 'Oral communication, behavioural judgement and suitability for safety-critical working.',
    icon: MessageSquare,
    tests: 'SJE, MMI',
  },
];

const downloadableResources = [
  {
    title: 'Psychometric Test Preparation Checklist',
    description:
      'A printable preparation checklist covering assessment-day readiness, practice planning and common mistakes to avoid.',
    type: 'PDF Checklist',
    icon: FileText,
    href: '/resources/psychometric-test-preparation-checklist',
  },
  {
    title: 'Common Forum Questions Answered',
    description:
      'Plain-English answers to the questions candidates repeatedly ask about train driver tests, interviews and retakes.',
    type: 'PDF Guide',
    icon: BookOpen,
    href: '/resources/common-forum-questions',
  },
];

export const ResourcesPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredArticles = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return articles.filter((article) => {
      const matchesCategory =
        activeCategory === 'All' || article.category === activeCategory;

      const searchableText = [
        article.title,
        article.description,
        article.category,
        ...article.tags,
      ]
        .join(' ')
        .toLowerCase();

      return matchesCategory && (!search || searchableText.includes(search));
    });
  }, [activeCategory, searchTerm]);

  const featuredArticles = articles.filter((article) => article.featured);

  return (
    <PageLayout>
      <SEO
        title="Train Driver Resources & Guides | Psychometric Tests, RIS-3751-TOM & Career Advice"
        description="Free train driver resources covering psychometric tests, RIS-3751-TOM, Group Bourdon, TEA-Occ, TRP, WAFV, ATAVT, 2HAND, SJE, MMI, attempt rules and career guidance."
        canonical="/resources"
      />

      <section className="relative overflow-hidden bg-[#071733]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,159,28,0.25),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.22),transparent_35%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
            <motion.div initial="hidden" animate="visible" variants={containerVariants}>
              <motion.div variants={fadeUp}>
                <Badge className="mb-5 bg-[#FF9F1C] text-[#071733] font-semibold">
                  Free train driver resources
                </Badge>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5"
              >
                Understand the process before you practise for it
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="text-lg sm:text-xl text-white/80 leading-relaxed max-w-3xl mb-8"
              >
                Practical guides for UK train driver applicants covering psychometric
                tests, RIS-3751-TOM, MMI, SJE, attempt rules, assessment preparation
                and the cognitive demands of safety-critical driving.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/register">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-[#FF9F1C] hover:bg-[#f59a18] text-[#071733] font-bold shadow-lg"
                  >
                    Start free practice
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>

                <Link to="/tests">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10"
                  >
                    View psychometric tests
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white/10 backdrop-blur border border-white/15 rounded-3xl p-5 sm:p-6 shadow-2xl"
            >
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['RIS-3751-TOM', 'Official assessment standard'],
                  ['3 attempts', 'Current reassessment structure'],
                  ['5 years', 'Assessment result validity'],
                  ['9 areas', 'Core selection criteria'],
                ].map(([stat, label]) => (
                  <div
                    key={stat}
                    className="rounded-2xl bg-white p-4 border border-white/20"
                  >
                    <p className="text-2xl font-bold text-[#0B1D51]">{stat}</p>
                    <p className="text-sm text-slate-600 mt-1">{label}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-2xl bg-[#FF9F1C] p-4 text-[#071733]">
                <div className="flex items-start gap-3">
                  <Award className="w-6 h-6 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-bold">Built for serious applicants</p>
                    <p className="text-sm opacity-90 mt-1">
                      Use these guides to understand what each assessment is trying
                      to measure before moving into timed practice.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-10">
          <Card className="shadow-xl border-slate-200 rounded-2xl overflow-hidden">
            <CardContent className="p-4 sm:p-5">
              <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search resources, tests, MMI, SJE, Group Bourdon..."
                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0B1D51] text-slate-800"
                  />
                </div>

                <Link to="/online-assessment">
                  <Button className="w-full md:w-auto bg-[#0B1D51] hover:bg-[#12306f] text-white">
                    Online assessment help
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>

              <div className="flex gap-2 overflow-x-auto pt-4 pb-1">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition ${
                      activeCategory === category
                        ? 'bg-[#0B1D51] text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-14 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-10"
          >
            <Badge className="mb-4 bg-[#0B1D51]/10 text-[#0B1D51]">
              Featured articles
            </Badge>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51]">
                  Essential reading for aspiring train drivers
                </h2>
                <p className="mt-3 text-slate-600 max-w-3xl">
                  Start with the official standard, then understand the assessment
                  process, then check the attempt and reassessment rules.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid lg:grid-cols-3 gap-6"
          >
            {featuredArticles.map((article) => {
              const Icon = article.icon;

              return (
                <motion.div key={article.slug} variants={fadeUp}>
                  <Link to={`/resources/${article.slug}`} className="block h-full">
                    <Card className="h-full border-slate-200 hover:border-[#FF9F1C]/60 hover:shadow-xl transition-all duration-300 group rounded-2xl overflow-hidden">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between gap-3 mb-4">
                          <Badge className="bg-[#FF9F1C]/15 text-[#9A5A00] border-0">
                            {article.priority}
                          </Badge>
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {article.readTime}
                          </span>
                        </div>

                        <div className="w-13 h-13 bg-[#0B1D51]/5 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-[#0B1D51] transition-colors">
                          <Icon className="w-7 h-7 text-[#0B1D51] group-hover:text-white transition-colors" />
                        </div>

                        <CardTitle className="font-heading text-xl text-[#0B1D51] group-hover:text-[#FF9F1C] transition-colors leading-snug">
                          {article.title}
                        </CardTitle>
                      </CardHeader>

                      <CardContent>
                        <CardDescription className="text-slate-600 leading-relaxed mb-5">
                          {article.description}
                        </CardDescription>

                        <div className="flex flex-wrap gap-2 mb-5">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        <div className="flex items-center text-sm font-bold text-[#0B1D51] group-hover:text-[#FF9F1C]">
                          Read guide
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-14 sm:py-16 bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-10 items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <Badge className="mb-4 bg-[#0B1D51]/10 text-[#0B1D51]">
                Assessment criteria
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] mb-4">
                What the train driver assessments are looking for
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                The assessment process is not random. It is designed around
                safety-related cognitive, psychomotor and behavioural aptitudes
                required for the train driver role.
              </p>

              <Link to="/tests">
                <Button className="bg-[#0B1D51] hover:bg-[#12306f] text-white">
                  Practise relevant skills
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid sm:grid-cols-2 gap-4"
            >
              {skillAreas.map((area) => {
                const Icon = area.icon;

                return (
                  <motion.div key={area.title} variants={fadeUp}>
                    <Card className="h-full border-slate-200 rounded-2xl hover:shadow-md transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-11 h-11 rounded-xl bg-[#0B1D51]/5 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-[#0B1D51]" />
                          </div>
                          <div>
                            <h3 className="font-heading font-bold text-[#0B1D51] mb-1">
                              {area.title}
                            </h3>
                            <p className="text-sm text-slate-600 leading-relaxed mb-3">
                              {area.text}
                            </p>
                            <p className="text-xs font-semibold text-[#FF9F1C]">
                              Relevant to: {area.tests}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-8"
          >
            <Badge className="mb-4 bg-[#0B1D51]/10 text-[#0B1D51]">
              Resource library
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51]">
              All train driver articles
            </h2>
            <p className="mt-3 text-slate-600">
              Browse practical guides by topic, or use the search box above to find
              a specific test, rule or stage of recruitment.
            </p>
          </motion.div>

          {filteredArticles.length > 0 ? (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-4"
            >
              {filteredArticles.map((article) => {
                const Icon = article.icon;

                return (
                  <motion.div key={article.slug} variants={fadeUp}>
                    <Link to={`/resources/${article.slug}`} className="block">
                      <Card className="border-slate-200 hover:border-[#FF9F1C]/60 hover:shadow-lg transition-all duration-300 group rounded-2xl">
                        <CardContent className="p-5 sm:p-6">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-[#0B1D51]/5 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#0B1D51] transition-colors">
                              <Icon className="w-6 h-6 text-[#0B1D51] group-hover:text-white transition-colors" />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <Badge variant="outline" className="text-xs">
                                  {article.category}
                                </Badge>
                                <span className="text-xs text-slate-500 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {article.readTime}
                                </span>
                              </div>

                              <h3 className="font-heading text-lg sm:text-xl font-bold text-[#0B1D51] group-hover:text-[#FF9F1C] transition-colors mb-2">
                                {article.title}
                              </h3>

                              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
                                {article.description}
                              </p>

                              <div className="flex flex-wrap gap-2">
                                {article.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#FF9F1C] group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            <Card className="border-dashed border-slate-300 rounded-2xl">
              <CardContent className="p-8 text-center">
                <Search className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <h3 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
                  No matching resources found
                </h3>
                <p className="text-slate-600 mb-4">
                  Try searching for a test name, assessment stage or broader topic.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setActiveCategory('All');
                  }}
                >
                  Reset filters
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      <section className="py-14 sm:py-16 bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-8 items-stretch">
            <Card className="rounded-3xl border-slate-200 overflow-hidden">
              <CardHeader>
                <Badge className="w-fit bg-[#0B1D51]/10 text-[#0B1D51]">
                  Free downloads
                </Badge>
                <CardTitle className="font-heading text-2xl text-[#0B1D51]">
                  Practical resources you can use while preparing
                </CardTitle>
                <CardDescription>
                  Save these resources and use them alongside your practice routine.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {downloadableResources.map((resource) => {
                  const Icon = resource.icon;

                  return (
                    <Link key={resource.title} to={resource.href} className="block">
                      <div className="flex items-start gap-4 p-4 rounded-2xl border border-slate-200 hover:border-[#FF9F1C]/60 hover:bg-white transition group">
                        <div className="w-11 h-11 rounded-xl bg-[#0B1D51]/5 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-[#0B1D51]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-[#0B1D51] group-hover:text-[#FF9F1C]">
                              {resource.title}
                            </h3>
                            <Badge variant="outline" className="text-[10px]">
                              {resource.type}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">
                            {resource.description}
                          </p>
                        </div>
                        <Download className="w-5 h-5 text-slate-400 group-hover:text-[#FF9F1C]" />
                      </div>
                    </Link>
                  );
                })}
              </CardContent>
            </Card>

            <Card className="rounded-3xl border-0 bg-[#0B1D51] text-white overflow-hidden relative">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,159,28,0.28),transparent_35%)]" />
              <CardContent className="relative p-6 sm:p-8 h-full flex flex-col justify-between">
                <div>
                  <Badge className="mb-5 bg-[#FF9F1C] text-[#071733] font-bold">
                    Preparation pathway
                  </Badge>

                  <h2 className="font-heading text-3xl font-bold mb-4">
                    Read the guide. Understand the skill. Practise under pressure.
                  </h2>

                  <div className="space-y-4 mt-6">
                    {[
                      'Understand what each assessment measures.',
                      'Practise relevant skills with timed, interactive tasks.',
                      'Review weak areas before paying for more preparation.',
                    ].map((item) => (
                      <div key={item} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#FF9F1C] mt-0.5 flex-shrink-0" />
                        <p className="text-white/85">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Link to="/register">
                    <Button className="w-full sm:w-auto bg-[#FF9F1C] hover:bg-[#f59a18] text-[#071733] font-bold">
                      Get 5 free attempts
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>

                  <Link to="/pricing">
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10"
                    >
                      View plans
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="rounded-3xl border-slate-200 shadow-xl overflow-hidden">
            <CardContent className="p-6 sm:p-8">
              <div className="grid md:grid-cols-[auto_1fr_auto] gap-5 items-center">
                <div className="w-16 h-16 rounded-2xl bg-[#FF9F1C]/15 flex items-center justify-center">
                  <Train className="w-8 h-8 text-[#0B1D51]" />
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#FF9F1C]" />
                    <p className="text-sm font-bold uppercase tracking-wide text-[#FF9F1C]">
                      Next step
                    </p>
                  </div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B1D51]">
                    Turn reading into measurable preparation
                  </h2>
                  <p className="text-slate-600 mt-2">
                    Use the free resources to understand the process, then move into
                    structured practice for concentration, vigilance, perception,
                    memory, reasoning and behavioural judgement.
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <Link to="/register">
                    <Button className="bg-[#0B1D51] hover:bg-[#12306f] text-white w-full">
                      Start free
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/tests">
                    <Button variant="outline" className="w-full">
                      View tests
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </PageLayout>
  );
};

export default ResourcesPage;